import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import ModalMAIQ from 'app/components/ModalMAIQ';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import { extractSegmentAttentionContext } from 'app/utils/hilSegmentsAttentionUtils';
import DealerValuesEntityPanel from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/components/DealerValuesEntityPanel';
import DealerSpecialsEntityPanel from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/components/DealerSpecialsEntityPanel';
import FooterImageEntityPanel from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/components/FooterImageEntityPanel';
import OffersEntityPanel from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/components/OffersEntityPanel';
import TemplatePendingApprovalEntityPanel from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/components/TemplatePendingApprovalEntityPanel';
import TemplateIntentEntityPanel from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/components/TemplateIntentEntityPanel';
import TemplateUploadEntityPanel from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/components/TemplateUploadEntityPanel';
import UnknownEntityPanel from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/components/UnknownEntityPanel';
import type { ResolveEntitiesModalMAIQProps } from './types';
import {
  entityTypeLabel,
  extractEntitiesFromRow,
  extractResolutionFromRow,
  normalizeEntityType,
  parseResolveSteps,
} from './utils';

function a11yProps(index: number) {
  return {
    id: `resolve-entity-tab-${index}`,
    'aria-controls': `resolve-entity-tabpanel-${index}`,
  };
}

export default function ResolveEntitiesModalMAIQ({
  open,
  onClose,
  dealerId,
  row,
  onSuccess,
  onResolveClick,
  isResolving: isResolvingProp,
}: ResolveEntitiesModalMAIQProps) {
  const context = useMemo(() => extractSegmentAttentionContext(row), [row]);
  const entitiesRaw = useMemo(() => extractEntitiesFromRow(row), [row]);
  const resolution = useMemo(() => extractResolutionFromRow(row), [row]);
  const isResolving =
    isResolvingProp ??
    Boolean(
      row && typeof row === 'object' && (row as { __retrying?: unknown }).__retrying,
    );

  const resolveSteps = useMemo(
    () => parseResolveSteps(resolution),
    [resolution],
  );

  const STEP_CARD_PY = 1.25;
  const STEP_CIRCLE_SIZE = 18;

  const hiddenReasonCodes = useMemo(
    () => new Set(['NO_TEMPLATE_FOUND', 'VALUE_STEP_ALL_STATIC_ALREADY_SENT']),
    [],
  );
  const shouldHideEntityTabs = Boolean(
    context.reason_code && hiddenReasonCodes.has(context.reason_code),
  );
  const isManualResolution = String(resolution?.mode ?? '').toLowerCase() === 'manual';
  const templateUploadInitialType = useMemo(() => {
    const fromEntities =
      entitiesRaw.find(
        (e) => String(e?.entity_type ?? '').toLowerCase() === 'template_intent',
      )?.entity_ids?.[0] ?? '';
    if (fromEntities) return fromEntities;

    const meta = row && typeof row === 'object' ? (row as any).metadata : null;
    const fromMeta = Array.isArray(meta?.template_intents) ? meta.template_intents[0] : '';
    if (fromMeta) return String(fromMeta).trim();

    const actions = resolution?.entity_actions ?? [];
    if (Array.isArray(actions)) {
      const match = actions.find((a: any) => {
        const t = String(a?.entity_type ?? '').toLowerCase();
        return t === 'template_intent';
      });
      if (match?.entity_id) return String(match.entity_id).trim();
    }
    return '';
  }, [entitiesRaw, resolution?.entity_actions, row]);

  const entities = shouldHideEntityTabs
    ? isManualResolution
      ? [{ entity_type: 'template_upload', entity_ids: [] }]
      : []
    : entitiesRaw;

  const hasTabs = entities.length > 1;
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      return;
    }
    if (activeTab >= entities.length) {
      setActiveTab(0);
    }
  }, [activeTab, entities.length, open]);

  const selectedEntity = entities[activeTab] ?? entities[0] ?? null;
  const selectedEntityType = selectedEntity?.entity_type ?? '';
  const selectedIds =
    Array.isArray(selectedEntity?.entity_ids) ? selectedEntity!.entity_ids! : [];

  const renderEntityPanel = () => {
    const key = normalizeEntityType(selectedEntityType);
    if (key === 'dealer_value') {
      return <DealerValuesEntityPanel dealerId={dealerId} allowedIds={selectedIds} />;
    }
    if (key === 'dealer_special' || key === 'dealer_specials') {
      return <DealerSpecialsEntityPanel dealerId={dealerId} allowedIds={selectedIds} />;
    }
    if (key === 'footer_image' || key === 'footer_images') {
      return <FooterImageEntityPanel dealerId={dealerId} allowedIds={selectedIds} />;
    }
    if (key === 'offer' || key === 'offers') {
      return <OffersEntityPanel dealerId={dealerId} allowedIds={selectedIds} />;
    }
    if (key === 'template' || key === 'templates') {
      return (
        <TemplatePendingApprovalEntityPanel
          open={open}
          dealerId={dealerId}
          templateIds={selectedIds}
          context={context}
          onSuccess={onSuccess}
        />
      );
    }
    if (key === 'template_upload') {
      return (
        <TemplateUploadEntityPanel
          dealerId={dealerId}
          initialTemplateType={templateUploadInitialType}
          filterSegmentName={context.segment_name}
        />
      );
    }
    if (key === 'template_intent') {
      return <TemplateIntentEntityPanel dealerId={dealerId} context={context} />;
    }
    return <UnknownEntityPanel entityType={selectedEntityType} />;
  };

  const hasAnyContent = entities.length > 0 || Boolean(resolution);

  return (
    <ModalMAIQ
      open={open}
      onClose={onClose}
      customStyle={{
        width: '96vw',
        maxWidth: 1500,
        height: '90vh',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box className="modal-header" sx={{ px: 2, py: 1.5 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          Resolve actions
        </Typography>
      </Box>

      <Box
        sx={{
          p: 2,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ flexShrink: 0 }}>
          {context.segment_name ? (
            <Chip size="small" label={`Segment: ${context.segment_name}`} />
          ) : null}
          {context.issue ? (
            <Chip size="small" label={`Issue: ${context.issue}`} variant="outlined" />
          ) : null}
          {context.reason_code ? (
            <Chip
              size="small"
              label={`Reason: ${context.reason_code}`}
              variant="outlined"
            />
          ) : null}
        </Stack>

        {resolution?.summary ? (
          <Alert severity="info" sx={{ borderRadius: 2, flexShrink: 0 }}>
            {resolution.summary}
          </Alert>
        ) : null}

        {resolveSteps.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Steps
            </Typography>

            <Stack spacing={0.5}>
              {resolveSteps.map((step, idx) => (
                <Box
                  key={`${step.action || 'step'}-${step.order}-${idx}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                  }}
                >
                  {/* Timeline rail */}
                  <Box
                    sx={{
                      width: 22,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      flexShrink: 0,
                      pt: 0.75,
                    }}
                  >
                    {/* Step circle */}
                    <Box
                      sx={{
                        width: STEP_CIRCLE_SIZE,
                        height: STEP_CIRCLE_SIZE,
                        borderRadius: 999,
                        border: '3px solid',
                        borderColor: 'primary.main',
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: 999,
                          bgcolor: 'primary.main',
                        }}
                      />
                    </Box>

                    {/* Connector line */}
                    {idx < resolveSteps.length - 1 ? (
                      <Box
                        sx={{
                          width: 2,
                          flex: 1,
                          minHeight: 6,
                          bgcolor: 'primary.main',
                          borderRadius: 999,
                          mt: 0.25,
                        }}
                      />
                    ) : null}
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      {step.required ? (
                        <Tooltip title="Required step">
                          <Chip
                            size="small"
                            label="Required"
                            color="warning"
                            variant="outlined"
                            sx={{
                              height: 20,
                              '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      ) : null}

                      <Typography
                        variant="body2"
                        component="div"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.4,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5 }}
                        >
                          {step.order}.
                        </Box>
                        {step.label || step.action || 'Step'}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Stack>
          </Box>
        ) : null}

        {!hasAnyContent ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No resolve details are available for this attention row yet.
          </Alert>
        ) : entities.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            This attention row has resolve metadata, but no entities were provided.
          </Alert>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {hasTabs ? (
              <Tabs
                value={activeTab}
                onChange={(_, next) => setActiveTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ flexShrink: 0 }}
              >
                {entities.map((e, idx) => (
                  <Tab
                    key={`${e.entity_type ?? 'entity'}-${idx}`}
                    label={entityTypeLabel(e.entity_type ?? '')}
                    {...a11yProps(idx)}
                  />
                ))}
              </Tabs>
            ) : null}

            <Box
              role="tabpanel"
              id={`resolve-entity-tabpanel-${activeTab}`}
              aria-labelledby={`resolve-entity-tab-${activeTab}`}
              sx={{
                pt: hasTabs ? 1.5 : 0,
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {renderEntityPanel()}
            </Box>
          </Box>
        )}
      </Box>

      {onResolveClick ? (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: '1px solid',
            borderTopColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <ButtonMAIQ
            color="primary"
            onClick={onResolveClick}
            disabled={isResolving}
            startIcon={
              isResolving ? (
                <CircularProgress size={16} thickness={5} />
              ) : (
                <TaskAltOutlinedIcon fontSize="small" />
              )
            }
          >
            {isResolving ? 'Resolving…' : 'Resolve'}
          </ButtonMAIQ>
        </Box>
      ) : null}
    </ModalMAIQ>
  );
}

export type { ResolveEntitiesModalMAIQProps } from './types';

